import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonOutline as PersonIcon,
  MailOutline as MailIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useCityInView } from '../../hooks/city/useHubCity';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage, translateViolations } from '../../utils/apiMessages';
import ResendConfirmation from './ResendConfirmation';

/**
 * Mirrors Username::PATTERN on the backend, which remains the authority.
 *
 * A name, roughly: letters from any alphabet, digits, and the separators
 * names use. It may end with a dot, because "Marie D." is how a surname gets
 * abbreviated. Surrounding and repeated whitespace is tolerated because the
 * backend trims and collapses it.
 */
const USERNAME_PATTERN = /^\s*[\p{L}\p{N}](?:(?:\s+|[.'’_-])?[\p{L}\p{M}\p{N}])*\.?\s*$/u;

/** Anything the reader cannot see: zero-width spaces, bidi overrides. */
const INVISIBLE_CHARACTER = /\p{C}/u;
const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 30;
export const PASSWORD_MIN_LENGTH = 8;

/** The fields this form shows, and so the only ones an error can sit under. */
const FIELDS = ['username', 'email', 'password', 'citySlug', 'neighborhoodUuid', 'acceptTerms'];

/**
 * Same rules as the Username value object and the password constraint on the
 * backend, checked here so the resident sees the problem before a round-trip.
 * The backend remains the authority.
 */
export function validateRegistration({ username, email, password }) {
  const errors = {};

  if (!username) {
    errors.username = "Choisissez un nom d'utilisateur";
  } else if (
    username.trim().length < USERNAME_MIN_LENGTH
    || username.trim().length > USERNAME_MAX_LENGTH
  ) {
    errors.username = `Entre ${USERNAME_MIN_LENGTH} et ${USERNAME_MAX_LENGTH} caractères`;
  } else if (INVISIBLE_CHARACTER.test(username)) {
    // Worth its own message: "format invalide" is impossible to act on when
    // the offending character cannot be seen.
    errors.username = 'Ce nom contient un caractère invisible. Retapez-le plutôt que de le coller.';
  } else if (!USERNAME_PATTERN.test(username)) {
    errors.username =
      'Lettres, chiffres, espaces, apostrophes, points, tirets et tirets bas. À commencer par une lettre ou un chiffre.';
  }

  if (!email) {
    errors.email = 'Entrez votre adresse email';
  }

  if (!password) {
    errors.password = 'Choisissez un mot de passe';
  } else if (password.length < PASSWORD_MIN_LENGTH) {
    errors.password = `Au moins ${PASSWORD_MIN_LENGTH} caractères`;
  }

  return errors;
}

/**
 * One sign-up form for everybody.
 *
 * There used to be two, with two endpoints and two sets of copy, which forced
 * a stranger to the site to classify themselves before they had seen it — and
 * left a resident who later wanted to offer a service with no way through. A
 * professional is a resident who also offers something, so it is a checkbox.
 */
function RegisterForm() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [professional, setProfessional] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [citySlug, setCitySlug] = useState('');
  const [neighborhoodUuid, setNeighborhoodUuid] = useState('');
  const [neighborhoods, setNeighborhoods] = useState([]);
  const { register } = useAuth();
  const { cities } = useCityInView();
  // The address the confirmation link went to, once the account exists.
  const [sentTo, setSentTo] = useState('');

  // The form gives way to a card a fraction of its height, on the same route,
  // so ScrollToTop never fires: the reader was left at the height of the
  // submit button, looking at the foot of the page, the message above them.
  useEffect(() => {
    if (sentTo) {
      window.scrollTo(0, 0);
    }
  }, [sentTo]);

  // Shown even with one city open, so the resident sees where their account
  // will belong and the neighbourhood below has a city to list its own. With
  // one open it is filled in; with several there is no honest default.
  useEffect(() => {
    if (!citySlug && 1 === cities.length) {
      setCitySlug(cities[0].slug);
    }
  }, [citySlug, cities]);

  useEffect(() => {
    if (!citySlug) {
      setNeighborhoods([]);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const response = await apiCall(`/cities/${citySlug}/neighborhoods`, { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (!cancelled) {
          setNeighborhoods(data.data || []);
        }
      } catch {
        // The form still works without the list; the field is optional.
        if (!cancelled) {
          setNeighborhoods([]);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [citySlug]);

  const handleCityChange = (e) => {
    setCitySlug(e.target.value);
    // A neighbourhood of the city they just left is not one of theirs.
    setNeighborhoodUuid('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const errors = validateRegistration({ username, email, password });

    if (!citySlug) {
      errors.citySlug = 'Choisissez votre ville';
    }

    if (!acceptTerms) {
      errors.acceptTerms = 'Acceptez les conditions d’utilisation pour créer votre compte';
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setLoading(true);
    const result = await register({
      username,
      email,
      password,
      professional,
      acceptTerms,
      citySlug,
      neighborhoodUuid: neighborhoodUuid || null,
    });
    setLoading(false);

    // No session yet: the account signs in once the address is confirmed.
    if (result.success) {
      setSentTo(email.trim());
      return;
    }

    // The API validates in English; a resident reads the same thing in French.
    const violations = translateViolations(result.violations);
    setFieldErrors(violations);

    // The banner carries whatever no field could: nothing came back keyed by
    // a field, or it was keyed by one this form does not show — a rejected
    // "roles" among them, which would otherwise fail in silence.
    const shown = Object.keys(violations).filter((field) => FIELDS.includes(field));

    if (0 === shown.length) {
      setError(translateApiMessage(result.error));
    }
  };

  if (sentTo) {
    return (
      <Card sx={{ maxWidth: 640, width: '100%', borderRadius: '22px' }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, alignItems: 'flex-start' }}>
            <Typography variant="h2" component="h1">
              Vérifiez votre boîte mail
            </Typography>
            <Typography variant="body1">
              Votre compte est créé. Nous venons d’envoyer un lien à{' '}
              {/* In one piece: an address cut in two is one somebody checks
                  twice before trusting it. Too long for the line, it moves
                  to the next one whole. */}
              <Box component="strong" sx={{ whiteSpace: 'nowrap' }}>
                {sentTo}
              </Box>
              &nbsp;: ouvrez-le pour confirmer votre adresse, puis connectez-vous.
            </Typography>
            <Typography variant="body2">
              Rien reçu au bout de quelques minutes&nbsp;? Regardez dans les indésirables, ou
              demandez un nouveau lien.
            </Typography>
            <ResendConfirmation email={sentTo} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ maxWidth: 640, width: '100%', borderRadius: '22px' }}>
      <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3 }}>
          <Typography variant="h2" component="h1">
            Créer votre compte
          </Typography>
          <Typography variant="subtitle1">
            Une seule inscription, que vous veniez lire entre voisins ou faire connaître votre
            activité.
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
        >
          <TextField
            label="Nom d'utilisateur"
            id="username"
            placeholder="marie.dupont"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={Boolean(fieldErrors.username)}
            helperText={fieldErrors.username || 'Le nom que vos voisins verront'}
            fullWidth
            variant="outlined"
            disabled={loading}
            autoComplete="username"
            autoFocus
            inputProps={{ maxLength: USERNAME_MAX_LENGTH }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Adresse email"
            type="email"
            id="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(fieldErrors.email)}
            helperText={
              fieldErrors.email || 'Sert à vous connecter. Jamais affiché aux autres habitants.'
            }
            fullWidth
            variant="outlined"
            disabled={loading}
            autoComplete="email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Mot de passe"
            type={showPassword ? 'text' : 'password'}
            id="password"
            placeholder="Au moins 8 caractères"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password || ' '}
            fullWidth
            variant="outlined"
            disabled={loading}
            autoComplete="new-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    edge="end"
                    title={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* After the three things every account needs, because it is the
              one field that is not about them: it is where they live, and it
              only shows at all when more than one city is open. */}
          <TextField
            select
            label="Votre ville"
            id="citySlug"
            value={citySlug}
            onChange={handleCityChange}
            error={Boolean(fieldErrors.citySlug)}
            helperText={fieldErrors.citySlug || 'La ville que vous verrez en vous connectant.'}
            fullWidth
            disabled={loading}
          >
            {cities.map((one) => (
              <MenuItem key={one.slug} value={one.slug}>
                {one.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Votre quartier"
            id="neighborhoodUuid"
            value={neighborhoodUuid}
            onChange={(e) => setNeighborhoodUuid(e.target.value)}
            error={Boolean(fieldErrors.neighborhoodUuid)}
            helperText={
              fieldErrors.neighborhoodUuid
              || 'Facultatif — vous pourrez le choisir plus tard, depuis votre compte.'
            }
            fullWidth
            disabled={loading || !citySlug}
          >
            <MenuItem value="">Je ne sais pas encore</MenuItem>
            {neighborhoods.map((neighborhood) => (
              <MenuItem key={neighborhood.uuid} value={neighborhood.uuid}>
                {neighborhood.name}
              </MenuItem>
            ))}
          </TextField>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              p: 2.5,
              borderRadius: '16px',
              border: '1.5px solid',
              borderColor: professional ? '#e0b89e' : 'divider',
              bgcolor: professional ? 'warning.light' : 'background.default',
            }}
          >
            <FormControlLabel
              sx={{ mr: 0, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  checked={professional}
                  onChange={(e) => setProfessional(e.target.checked)}
                  disabled={loading}
                  inputProps={{ 'aria-describedby': 'professional-hint' }}
                  sx={{ pt: 0.25 }}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  Je tiens un commerce / Je propose des services
                </Typography>
              }
            />
            {/* A <p> inside a <p>, which is what a Typography renders, is
                invalid markup the browser silently unnests. */}
            <Typography id="professional-hint" variant="body2" component="div" sx={{ pl: 5.25 }}>
              Votre fiche se crée juste après, et reste invisible jusqu’à sa validation.
              <Box component="span" sx={{ display: 'block', mt: 0.75 }}>
                Vous restez un habitant comme les autres — et vous pouvez cocher cette case plus
                tard, depuis votre compte.
              </Box>
            </Typography>
          </Box>

          {/* Unticked, and required: an acceptance the resident did not give
              themselves proves nothing. The terms open in a new tab so the
              form keeps what was typed. */}
          <FormControl error={Boolean(fieldErrors.acceptTerms)}>
            <FormControlLabel
              sx={{ mr: 0, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  id="acceptTerms"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  disabled={loading}
                  sx={{ pt: 0.25 }}
                />
              }
              label={
                <Typography variant="body2" component="span">
                  J’accepte les{' '}
                  <Link
                    component={RouterLink}
                    to="/conditions-utilisation"
                    target="_blank"
                    rel="noopener"
                  >
                    conditions d’utilisation
                  </Link>
                </Typography>
              }
            />
            {fieldErrors.acceptTerms && (
              <FormHelperText sx={{ ml: 4.25 }}>{fieldErrors.acceptTerms}</FormHelperText>
            )}
          </FormControl>

          {error && <Alert severity="error">{error}</Alert>}

          <Button type="submit" variant="contained" size="large" fullWidth disabled={loading}>
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1.5, color: 'inherit' }} />
                Création…
              </>
            ) : (
              'Créer mon compte'
            )}
          </Button>

          <Typography variant="caption" sx={{ display: 'block' }}>
            Votre adresse électronique n’est jamais affichée aux autres habitants. Ce que nous
            faisons de vos données&nbsp;:{' '}
            <Link component={RouterLink} to="/mentions-legales" target="_blank" rel="noopener">
              mentions légales
            </Link>
            .
          </Typography>

          <Typography variant="body2" align="center">
            Vous avez déjà un compte&nbsp;?{' '}
            <Link component={RouterLink} to="/login">
              Se connecter
            </Link>
          </Typography>

        </Box>
      </CardContent>
    </Card>
  );
}

export default RegisterForm;
